import { translations, normalizeLang } from "./i18n";

// "0:00" under an hour, "1:02:03" at or past one hour — same shape as a
// native media player's time display.
export function formatDuration(seconds) {
  if (seconds == null || !isFinite(seconds)) return "0:00";
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// `transcription_lines` arrives from the backend already regrouped into
// caption-sized rows (~7s, sentence/pause-aware breaks) — this is what gets
// rendered, copied, and exported. The old `segments` array is now a frozen,
// server-side-only audit trail of the model's raw output and is never read
// here.
//
// Each word's `source_index` is its position in the flattened word list
// across the *original* `segments` in ASR order — the identity the PATCH
// endpoint edits against, which is NOT the same as its position in
// `transcription_lines`. The backend doesn't send this yet, so this derives
// it by flattening `transcription_lines` in order, which only lines up 1:1
// with the true source_index on a transcript that has never had a word
// dropped through this endpoint before (nothing missing yet, so flattened
// position === original position). Call this exactly once, right when a
// transcript is first loaded, and carry the result through local edit state
// from then on — never re-derive it from a later GET/PATCH response, since a
// transcript with prior deletions has gaps that re-flattening can't see and
// would silently renumber against the wrong words.
export function withSourceIndex(lines) {
  let cursor = 0;
  return lines.map((line) => ({
    ...line,
    words: line.words.map((word) => ({
      ...word,
      source_index: word.source_index ?? cursor++,
    })),
  }));
}

// Reword: same source_index, new text. A "split" (e.g. "waalbayt" ->
// "wa albayt") is just a reword whose text happens to contain a space — it
// stays one word entry sharing the original timing, so nothing special is
// needed here.
export function setWordText(lines, lineIndex, wordPos, newText) {
  const trimmed = newText.trim();
  if (!trimmed) return lines;
  return lines.map((line, i) => {
    if (i !== lineIndex) return line;
    return {
      ...line,
      words: line.words.map((word, j) => (j === wordPos ? { ...word, word: trimmed } : word)),
    };
  });
}

// Delete: there's no explicit delete op on the wire — a word is dropped just
// by leaving its source_index out of the PATCH payload. A line can't be sent
// with zero words, so a line emptied by its last word being deleted is
// dropped along with it.
export function deleteWord(lines, lineIndex, wordPos) {
  return lines
    .map((line, i) => {
      if (i !== lineIndex) return line;
      const words = line.words.filter((_, j) => j !== wordPos);
      if (words.length === 0) return { ...line, words };
      return { ...line, start: words[0].start, end: words[words.length - 1].end, words };
    })
    .filter((line) => line.words.length > 0);
}

// Splits one line into two at wordPos (that word becomes the first word of
// the new second line). Like the delete/reword ops, this is purely a local
// reshaping of the array — the words themselves, their order, and their
// timings are untouched, so it can't violate the backend's strictly-
// increasing source_index rule. wordPos must be >= 1: splitting before the
// first word wouldn't produce two non-empty lines.
export function splitLine(lines, lineIndex, wordPos) {
  const line = lines[lineIndex];
  if (wordPos < 1 || wordPos >= line.words.length) return lines;
  const before = line.words.slice(0, wordPos);
  const after = line.words.slice(wordPos);
  const firstLine = { start: before[0].start, end: before[before.length - 1].end, words: before };
  const secondLine = { start: after[0].start, end: after[after.length - 1].end, words: after };
  return [...lines.slice(0, lineIndex), firstLine, secondLine, ...lines.slice(lineIndex + 1)];
}

// Merges a line with its neighbor above or below into one line, concatenating
// their words in original order — same reasoning as splitLine: no reorder,
// so source_index stays strictly increasing.
//
// `anchorToTarget` controls which line's start survives onto the merged
// line. Transcript lines send their start/end to the backend and use it for
// the timestamp label and seek — that must stay the true earliest word's
// time, so callers leave this off. Translation lines drop start/end on the
// wire (see buildTranslatedLinesPayload) and only use it locally to decide
// which source cue a translated line renders under, so translation merges
// pass true: "merge with line below" is expected to fold the current line
// into the line below and keep that line's position, not pull the line
// below up into the current one.
export function mergeLines(lines, lineIndex, direction, { anchorToTarget = false } = {}) {
  const otherIndex = direction === "above" ? lineIndex - 1 : lineIndex + 1;
  if (otherIndex < 0 || otherIndex >= lines.length) return lines;
  const [firstIndex, secondIndex] = lineIndex < otherIndex ? [lineIndex, otherIndex] : [otherIndex, lineIndex];
  const words = [...lines[firstIndex].words, ...lines[secondIndex].words];
  const start = anchorToTarget ? lines[otherIndex].start : words[0].start;
  const merged = { start, end: words[words.length - 1].end, words };
  return [...lines.slice(0, firstIndex), merged, ...lines.slice(secondIndex + 1)];
}

// AI review suggestions carry the flagged word's own start/end (its real
// audio timestamp) rather than a source_index, and the two endpoints don't
// guarantee bit-identical float serialization for the same underlying value
// — so words are matched by proximity, not ===. 50ms is comfortably smaller
// than any real word's duration.
const SUGGESTION_MATCH_TOLERANCE = 0.05;

function findSuggestionWord(lines, suggestion) {
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const wordPos = lines[lineIndex].words.findIndex(
      (word) =>
        Math.abs(word.start - suggestion.start) < SUGGESTION_MATCH_TOLERANCE &&
        Math.abs(word.end - suggestion.end) < SUGGESTION_MATCH_TOLERANCE
    );
    if (wordPos !== -1) return { lineIndex, wordPos };
  }
  return null;
}

// Groups pending suggestions by the line they land in: lineIndex -> (wordPos
// -> suggestion). Matching is re-derived from live `lines` on every call
// (rather than cached against a lineIndex the suggestion "remembers") so a
// suggestion still finds its word correctly after a split/merge/resegment —
// none of those ever change a word's start/end, only which line it's bucketed
// into. A suggestion whose word can't be found (e.g. locally deleted since)
// is dropped rather than shown with nowhere to render.
export function groupSuggestionsByLine(lines, suggestions) {
  const byLine = new Map();
  suggestions.forEach((suggestion) => {
    const match = findSuggestionWord(lines, suggestion);
    if (!match) return;
    if (!byLine.has(match.lineIndex)) byLine.set(match.lineIndex, new Map());
    byLine.get(match.lineIndex).set(match.wordPos, suggestion);
  });
  return byLine;
}

// Applies a batch of accepted corrections in one pass, so N accepted words
// become one lines/state update instead of N.
export function applySuggestionWords(lines, entries) {
  return entries.reduce((acc, { lineIndex, wordPos, correctedWord }) => setWordText(acc, lineIndex, wordPos, correctedWord), lines);
}

// A transcript's still-pending suggestions can span more than one past review
// run (an old run's rows aren't deleted just because a newer run exists), but
// the product model is "last review wins" — once a newer run has completed,
// its predecessor's leftovers are no longer shown or actionable. Picks
// whichever review produced the most recently created suggestion and returns
// only *that* review's still-pending rows.
export function latestReviewPending(suggestions) {
  if (suggestions.length === 0) return [];
  const latest = suggestions.reduce((a, b) => (a.created_at > b.created_at ? a : b));
  return suggestions.filter((s) => s.review === latest.review && s.status === "pending");
}

// Reflows every word across `lines`, in source order, into fresh lines of
// exactly `wordsPerLine` words each (the final line takes the remainder) —
// the "resize" feature's alternative to the backend's sentence/pause-aware
// grouping. Like splitLine/mergeLines, this only ever reshuffles bucket
// boundaries over the same ordered word sequence, so it can't violate the
// backend's strictly-increasing source_index rule.
export function resegmentByWordCount(lines, wordsPerLine) {
  const words = lines.flatMap((line) => line.words);
  if (words.length === 0) return [];
  const n = Math.max(1, Math.min(12, Math.round(wordsPerLine)));
  const result = [];
  for (let i = 0; i < words.length; i += n) {
    const chunk = words.slice(i, i + n);
    result.push({ start: chunk[0].start, end: chunk[chunk.length - 1].end, words: chunk });
  }
  return result;
}

// A line's text reconstructed from its (possibly locally edited) words,
// rather than trusting a stale `text` field.
export function lineText(line) {
  return line.words.map((word) => word.word.trim()).join(" ");
}

// Plain-text rendering of the current (possibly unsaved) lines, for the Copy
// action.
export function linesToPlainText(lines) {
  return lines.map(lineText).join("\n\n");
}

// Builds the `transcription_lines` PATCH payload from local edit state. Merge
// and split of *lines* aren't special actions either — just however these
// same word entries get bucketed into the array below. The backend requires
// source_index to be strictly increasing across the whole payload with no
// index reused or skipped-into; since local edits only ever change a word's
// text or drop it — never reorder or duplicate one — that invariant holds as
// long as `lines` originated from `withSourceIndex` and local edits alone.
export function buildTranscriptionLinesPayload(lines) {
  return lines.map((line) => ({
    start: line.start,
    end: line.end,
    text: lineText(line),
    words: line.words.map((word) => ({ source_index: word.source_index, word: word.word.trim() })),
  }));
}

// Translation edits use the same local line/word model as transcript edits,
// but their endpoint only needs the editable word text and the stable index
// assigned when the completed translation was loaded. Timings and line text
// remain server-owned and are deliberately left off the wire.
export function buildTranslatedLinesPayload(lines) {
  return lines
    .filter((line) => line.words.length > 0)
    .map((line) => ({
      words: line.words.map((word) => ({ source_index: word.source_index, word: word.word.trim() })),
    }));
}

// Returns the words whose trusted timestamps begin inside one display cue.
// Translation lines can be regrouped independently from transcript lines, so
// aligning by array index would hide or mis-pair text after a split/merge.
// Word timestamps survive those edits and remain the common coordinate system.
export function linesTextInTimeRange(lines, start, end, includeEnd = false) {
  return (lines ?? [])
    .flatMap((line) => line.words ?? [])
    .filter((word) => word.start >= start && (word.start < end || (includeEnd && word.start === end)))
    .map((word) => word.word.trim())
    .join(" ");
}

export const EXPORT_FORMATS = ["TXT", "JSON", "SRT", "VTT", "SBV", "ASS"];

// Formats that carry their own timing, so the timestamp toggle is fixed on.
export const TIMED_FORMATS = ["SRT", "VTT", "SBV", "ASS"];

export const FORMAT_EXTENSIONS = { TXT: "txt", JSON: "json", SRT: "srt", VTT: "vtt", SBV: "sbv", ASS: "ass" };

// Display names for a transcript/translation's own language, in the *UI's*
// current language — mirrors the existing transcribe.languageAr/languageEn
// picker labels rather than maintaining a second, English-only copy of them.
export function getLanguageNames(lang) {
  const tr = translations[normalizeLang(lang)].transcribe;
  return { ar: tr.languageAr, en: tr.languageEn };
}

// Target languages the translation endpoint accepts, in the order offered in
// the UI.
export const TRANSLATION_LANGUAGES = ["en", "ar"];

// Display helpers shared by every page that lists transcriptions (Home, a
// project) so the same file always reads the same way wherever it's shown.
// `lang` is optional (defaults to English) since most callers already have
// the transcript's own text/name and only fall back to this for the rare
// untitled+empty case.
export function transcriptName(t, lang) {
  if (t.name) return t.name;
  if (!t.text) return translations[normalizeLang(lang)].notifications.untitledTranscript;
  return t.text.length > 60 ? `${t.text.slice(0, 60)}…` : t.text;
}

export function textPreview(text, maxLength) {
  const characters = Array.from(text);
  if (characters.length <= maxLength) return text;
  return `${characters.slice(0, maxLength - 1).join("")}…`;
}

export function languageLabel(t) {
  return (t.language_detected || t.language_requested || "—").toUpperCase();
}

// `-u-nu-latn` pins Western digits under the Arabic locale (its default,
// Arabic-Indic digits, would be the only place in the app using them —
// nothing else here localizes numerals) while still getting Arabic month
// names and "ar" date ordering.
export function formatCreated(iso, lang) {
  const resolved = normalizeLang(lang);
  const locale = resolved === "ar" ? "ar-u-nu-latn" : "en-GB";
  const tr = translations[resolved].ui;
  const date = new Date(iso);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (sameDay(date, now)) {
    const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${tr.today} ${time}`;
  }
  if (sameDay(date, yesterday)) return tr.yesterday;
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function findProjectName(projects, projectId) {
  if (!projectId) return null;
  return projects.find((p) => p.id === projectId)?.name ?? null;
}

// A caption-sourced transcription gets the editor with the live video
// preview; everything else opens the plain transcript editor.
export function transcriptEditorPath(t) {
  return t.source === "caption" ? `/caption/${t.id}` : `/transcribe/${t.id}`;
}

// "00:01:31.50" — hours:minutes:seconds.hundredths. Only used for the TXT
// export's per-segment timestamp prefix; the player's own clock uses the
// coarser formatDuration above.
export function formatTimecode(seconds) {
  const whole = Math.max(0, seconds || 0);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = Math.floor(whole % 60);
  const hundredths = Math.floor((whole % 1) * 100);
  const pad = (value) => String(value).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}.${pad(hundredths)}`;
}

function subtitleTime(seconds, separator) {
  const whole = Math.max(0, seconds || 0);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = Math.floor(whole % 60);
  const millis = Math.floor((whole % 1) * 1000);
  const pad = (value, size = 2) => String(value).padStart(size, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}${separator}${pad(millis, 3)}`;
}

// SBV (YouTube's format) and ASS/SSA both write hours unpadded with a "."
// decimal separator, unlike SRT/VTT's zero-padded hours — SBV to millisecond
// precision, ASS to centiseconds.
function sbvTime(seconds) {
  const whole = Math.max(0, seconds || 0);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = Math.floor(whole % 60);
  const millis = Math.floor((whole % 1) * 1000);
  const pad = (value, size = 2) => String(value).padStart(size, "0");
  return `${hours}:${pad(minutes)}:${pad(secs)}.${pad(millis, 3)}`;
}

function assTime(seconds) {
  const whole = Math.max(0, seconds || 0);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = Math.floor(whole % 60);
  const centis = Math.floor((whole % 1) * 100);
  const pad = (value, size = 2) => String(value).padStart(size, "0");
  return `${hours}:${pad(minutes)}:${pad(secs)}.${pad(centis, 2)}`;
}

// Renders the transcript for a format. The same text feeds the export
// dialog's preview and the downloaded file, so what you see is what you get.
// Note the downloaded JSON format's own "segments" key is this app's public
// export schema, unrelated to (and intentionally not renamed with) the
// backend's `segments` field — changing it would break anyone already
// parsing exported files.
//
// `translations` is the same `{ [langCode]: translatedLines }` cache the
// editor page keeps; `selectedLangs` is which language codes to include,
// defaulting to just the original. Selecting only the original reproduces
// the exact output this function always produced (see the schema note
// above) — the multi-language shapes below only ever kick in once a second
// language is actually selected, so nothing already parsing a single-
// language export breaks.
export function renderTranscript(lines, languageCode, translations, selectedLangs, format, options) {
  const codes = selectedLangs && selectedLangs.length ? selectedLangs : [languageCode];
  const multi = codes.length > 1;

  const linesFor = (code) => (code === languageCode ? lines : translations?.[code] ?? []);
  // A single translated-language export should preserve that translation's
  // own edited segmentation. For a multilingual export, the first selected
  // language supplies the cue boundaries and every other language is aligned
  // into them by its trusted word timestamps.
  const baseCode = codes[0];
  const baseLines = linesFor(baseCode);

  const languageNames = getLanguageNames(options.uiLang);
  const labelFor = (code) =>
    options.languageLabelStyle === "name" ? languageNames[code] || code?.toUpperCase() : code?.toUpperCase();
  // Only meaningful with exactly one language selected — the "Language
  // labels" toggle. With several stacked in one block there's no way to
  // tell them apart without a label per line, so multi always labels
  // regardless of that toggle.
  const singleLabel = !multi && options.languageLabels ? labelFor(codes[0]) : null;

  function textFor(code, line, index) {
    if (code === baseCode) return lineText(line);
    return linesTextInTimeRange(linesFor(code), line.start, line.end, index === baseLines.length - 1);
  }

  // The cue/segment body: original single-language text (labeled or not,
  // exactly as before) when only one language is selected, otherwise every
  // selected language on its own bracketed line, in `selectedLangs`' order.
  function blockText(line, index) {
    if (!multi) {
      const text = textFor(codes[0], line, index);
      return singleLabel ? `[${singleLabel}] ${text}` : text;
    }
    return codes.map((code) => `[${labelFor(code)}] ${textFor(code, line, index)}`).join("\n");
  }

  if (format === "JSON") {
    if (!multi) {
      return JSON.stringify(
        {
          language: singleLabel ?? undefined,
          segments: baseLines.map((line, index) => ({
            start: Number(line.start.toFixed(2)),
            end: Number(line.end.toFixed(2)),
            text: textFor(codes[0], line, index),
          })),
        },
        null,
        2
      );
    }
    // No top-level "language" — with several selected it'd be ambiguous, so
    // each segment instead carries one key per selected language code.
    return JSON.stringify(
      {
        segments: baseLines.map((line, index) => {
          const segment = { start: Number(line.start.toFixed(2)), end: Number(line.end.toFixed(2)) };
          codes.forEach((code) => {
            segment[code] = textFor(code, line, index);
          });
          return segment;
        }),
      },
      null,
      2
    );
  }

  if (format === "SRT") {
    return baseLines
      .map((line, index) =>
        [index + 1, `${subtitleTime(line.start, ",")} --> ${subtitleTime(line.end, ",")}`, blockText(line, index)].join(
          "\n"
        )
      )
      .join("\n\n");
  }

  if (format === "VTT") {
    const cues = baseLines
      .map((line, index) =>
        [`${subtitleTime(line.start, ".")} --> ${subtitleTime(line.end, ".")}`, blockText(line, index)].join("\n")
      )
      .join("\n\n");
    return `WEBVTT\n\n${cues}`;
  }

  if (format === "SBV") {
    return baseLines
      .map((line, index) => [`${sbvTime(line.start)},${sbvTime(line.end)}`, blockText(line, index)].join("\n"))
      .join("\n\n");
  }

  if (format === "ASS") {
    const header = [
      "[Script Info]",
      "Title: Transcript",
      "ScriptType: v4.00+",
      "WrapStyle: 0",
      "PlayResX: 384",
      "PlayResY: 288",
      "",
      "[V4+ Styles]",
      "Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding",
      "Style: Default,Arial,20,&H00FFFFFF,&H000000FF,&H00000000,&H00000000,0,0,0,0,100,100,0,0,1,2,0,2,10,10,10,1",
      "",
      "[Events]",
      "Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text",
    ].join("\n");
    const events = baseLines
      .map((line, index) => {
        const text = blockText(line, index).replace(/\n/g, "\\N");
        return `Dialogue: 0,${assTime(line.start)},${assTime(line.end)},Default,,0,0,0,,${text}`;
      })
      .join("\n");
    return `${header}\n${events}`;
  }

  // TXT — the single-language label sits in the timestamp prefix line, same
  // as before; with several languages there's nowhere sensible to cram every
  // label into that one prefix, so instead each goes on its own bracketed
  // line in the body, same as every timed format above.
  return baseLines
    .map((line, index) => {
      const prefix = [options.timestamps ? formatTimecode(line.start) : null, singleLabel ? `[${singleLabel}]` : null]
        .filter(Boolean)
        .join(" ");
      const text = blockText(line, index);
      return prefix ? `${prefix}\n${text}` : text;
    })
    .join("\n\n");
}
